import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { selectToken } from '@/app/module/auth/store/selectors'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_SAVE_USER_SERVER } from '@/app/module/user/definitions'
import { getUser } from '@/app/module/user/store/actions'
import { selectUserDetails } from '@/app/module/user/store/selectors'
import { CommonUserType, UserUIStore } from '@/app/module/user/types'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

const isUserDetails = (userDetails: CommonUserType | string): userDetails is CommonUserType =>
  typeof userDetails === 'object' && userDetails !== null

export const getCatiContactsCallContactMode = (userDetails: CommonUserType | string) =>
  isUserDetails(userDetails) && Boolean(userDetails.uiStore?.catiContacts?.callContactMode)

export const useCatiContactsPreferences = () => {
  const dispatch = useAppDispatch()
  const token = selectToken() || ''
  const userDetails = useSelector(selectUserDetails) as CommonUserType | string
  const [saveUser, saveUserResult] = esApi.endpoints.saveUser.useMutation()

  const callContactMode = getCatiContactsCallContactMode(userDetails)

  const setCallContactMode = useCallback(
    async (enabled: boolean) => {
      const uiStore: UserUIStore = isUserDetails(userDetails) ? userDetails.uiStore || {} : {}

      try {
        await saveUser({
          body: {
            uiStore: {
              ...uiStore,
              catiContacts: {
                ...uiStore.catiContacts,
                callContactMode: enabled,
              },
            },
          },
        }).unwrap()
      } catch (error) {
        dispatch(
          createNotification({
            'save-cati-contacts-preferences': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SAVE_USER_SERVER),
            },
          }),
        )

        return false
      }

      if (token) {
        try {
          await dispatch(getUser({ token }))
        } catch {
          // Saving succeeded; a refresh failure should not report the preference save as failed.
        }
      }

      return true
    },
    [token, userDetails, dispatch, saveUser],
  )

  return {
    callContactMode,
    isSaving: saveUserResult.isLoading,
    setCallContactMode,
  }
}
